// AlternativeSuggestionCard — rendered in the dashboard action card slot
// when a consultation has an `alternativeSuggestion` field. Two variants:
// soft (sage/recommendation) and hard (peach/urgent).
// Spec: Sprint 6 of VOYA9_Frontend_Assessment_Multiprogram_Modes.md
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { apiPost } from "@/lib/api";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

export default function AlternativeSuggestionCard({ consultation, onResolved }) {
  const navigate = useNavigate();
  const alt = consultation?.alternativeSuggestion;
  const [showNotes, setShowNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!alt) return null;

  const isHard = alt.type === "hard";
  const physician = consultation.assignedPhysician;
  const physicianName = physician?.lastName ? `Dr. ${physician.lastName}` : "Your physician";
  const consultationId = consultation._id || consultation.id;

  const bg = isHard ? C.peach : C.sage;
  const fg = isHard ? C.peachDark : C.sageDark;
  const italic = isHard ? C.ink : C.sageDark;

  const act = async (verb) => {
    setBusy(true);
    try {
      await apiPost(`/consumer/consultations/${consultationId}/alternative/${verb}`, {});
      toast(verb === "accept" ? "Accepted — your physician has been notified." : "Noted. Dr. Chen will follow up.", "success");
      if (onResolved) await onResolved();
    } catch (err) {
      toast(err.detail || `Couldn't ${verb} right now. Try again.`, "error");
    } finally {
      setBusy(false);
    }
  };

  const declineHard = async () => {
    if (!window.confirm("Decline and refund your consult fee?")) return;
    setBusy(true);
    try {
      await apiPost(`/consumer/consultations/${consultationId}/alternative/decline`, { reason: "declined_alternative" });
      toast("Declined. Your consult fee will be refunded within 5-7 business days.", "success");
      if (onResolved) await onResolved(); else navigate("/app");
    } catch (err) {
      toast(err.detail || "Couldn't process decline. Please message Dr. Chen.", "error");
    } finally {
      setBusy(false);
    }
  };

  const suggestedTier = alt.suggestedTierName || alt.suggested?.tierName || "the alternative";
  const originalTier = consultation.originalRequest?.tierName || consultation.selectedTier?.tierName || "your original choice";
  const consultFeeDisplay = consultation.consultFeeDisplay || "$49";

  return (
    <section
      data-testid={`alternative-${isHard ? "hard" : "soft"}`}
      style={{ background: bg, color: fg, padding: "40px 24px 48px", position: "relative" }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <Eyebrow color={fg} testId="alternative-eyebrow">
          {isHard ? "★ WE CAN'T APPROVE YOUR ORIGINAL CHOICE" : "★ YOUR PHYSICIAN HAS A RECOMMENDATION"}
        </Eyebrow>

        {isHard ? (
          <Headline before="WE NEED TO" italic="go a different route." color={fg} italicColor={italic} size={48} className="mt-4" />
        ) : (
          <Headline before={physicianName.toUpperCase()} italic="suggests an alternative." color={fg} italicColor={italic} size={48} className="mt-4" />
        )}

        <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.65, color: fg, opacity: 0.92, maxWidth: 560 }}>
          {alt.physicianRationale ||
            (isHard
              ? `Based on your medical history, ${physicianName} cannot safely prescribe ${originalTier} for you. They recommend ${suggestedTier} instead.`
              : `Your original choice (${originalTier}) is a good option for you. ${physicianName} also wanted you to consider ${suggestedTier}, which they think may be more effective for your goals — but the choice is yours.`)}
        </p>

        {alt.physicianNotes && (
          <div style={{ marginTop: 14 }}>
            <button
              type="button"
              data-testid="alternative-read-notes"
              onClick={() => setShowNotes((v) => !v)}
              style={{ background: "transparent", border: 0, color: fg, fontSize: 12, fontWeight: 800, letterSpacing: "0.18em", cursor: "pointer", textDecoration: "underline" }}
            >
              {showNotes ? "HIDE NOTES" : `READ ${physicianName.toUpperCase()}'S FULL NOTES →`}
            </button>
            {showNotes && (
              <p style={{ marginTop: 12, padding: 16, background: "rgba(0,0,0,0.06)", color: fg, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontSize: 16, lineHeight: 1.6 }}>
                "{alt.physicianNotes}"
              </p>
            )}
          </div>
        )}

        <div style={{ marginTop: 32, fontSize: 12, fontWeight: 800, letterSpacing: "0.2em", color: fg, opacity: 0.7 }}>
          WHAT WOULD YOU LIKE TO DO?
        </div>

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <Button
            variant="primary"
            size="lg"
            block
            testId="alternative-accept-btn"
            onClick={() => act("accept")}
            disabled={busy}
          >
            {busy ? "…" : `ACCEPT ${suggestedTier.toUpperCase()} →`}
          </Button>

          {!isHard && (
            <Button
              variant="ghost"
              block
              testId="alternative-keep-btn"
              onClick={() => act("decline")}
              disabled={busy}
            >
              KEEP {originalTier.toUpperCase()}
            </Button>
          )}

          {isHard && (
            <button
              type="button"
              data-testid="alternative-decline-hard"
              onClick={declineHard}
              disabled={busy}
              style={{
                background: "transparent", border: `1.5px solid ${fg}`, color: fg,
                padding: "14px 20px", fontSize: 12, fontWeight: 800, letterSpacing: "0.2em", cursor: busy ? "default" : "pointer",
              }}
            >
              DECLINE — REFUND MY {consultFeeDisplay} CONSULT FEE
            </button>
          )}
        </div>

        <div style={{ marginTop: 18, textAlign: "center" }}>
          <Link
            to={`/app/care-team/${consultationId}`}
            data-testid="alternative-message-link"
            style={{ color: fg, fontSize: 13, textDecoration: "underline" }}
          >
            Have a question? Message {physicianName}
          </Link>
        </div>
      </div>
    </section>
  );
}
